import type ExcelJS from 'exceljs'
import { CsvParseError, type ParseCsvFile, type ParsedCsv } from './parse-csv-file'

function cellToString(cell: ExcelJS.Cell): string {
  const text = cell.text
  return typeof text === 'string' ? text.trim() : String(text ?? '').trim()
}

function firstVisibleSheet(workbook: ExcelJS.Workbook): ExcelJS.Worksheet | undefined {
  const visible = workbook.worksheets.filter(
    (sheet) => sheet.state !== 'hidden' && sheet.state !== 'veryHidden' && sheet.rowCount > 0,
  )
  return visible[0] ?? workbook.worksheets.find((sheet) => sheet.rowCount > 0)
}

export const parseXlsxFile: ParseCsvFile = async (file) => {
  const { default: ExcelJSLib } = await import('exceljs')

  let buffer: ArrayBuffer
  try {
    buffer = await file.arrayBuffer()
  } catch {
    throw new CsvParseError('The file could not be read.')
  }

  const workbook = new ExcelJSLib.Workbook()
  try {
    await workbook.xlsx.load(buffer)
  } catch {
    throw new CsvParseError('The Excel file could not be parsed.')
  }

  const sheet = firstVisibleSheet(workbook)
  if (!sheet) {
    throw new CsvParseError('The Excel file is empty.')
  }

  const populatedRows: ExcelJS.Row[] = []
  sheet.eachRow({ includeEmpty: false }, (row) => {
    populatedRows.push(row)
  })
  if (populatedRows.length === 0) {
    throw new CsvParseError('The Excel file is empty.')
  }

  const headerRow = populatedRows[0]
  let headerWidth = 0
  headerRow.eachCell({ includeEmpty: false }, (cell, columnNumber) => {
    if (cellToString(cell) !== '') headerWidth = Math.max(headerWidth, columnNumber)
  })
  if (headerWidth === 0) {
    throw new CsvParseError('The Excel file is missing a header row.')
  }

  const headers: string[] = []
  for (let column = 1; column <= headerWidth; column++) {
    headers.push(cellToString(headerRow.getCell(column)))
  }

  const rows: string[][] = []
  for (let index = 1; index < populatedRows.length; index++) {
    const source = populatedRows[index]
    const cells: string[] = []
    let hasValue = false
    for (let column = 1; column <= headerWidth; column++) {
      const value = cellToString(source.getCell(column))
      if (value !== '') hasValue = true
      cells.push(value)
    }
    if (hasValue) rows.push(cells)
  }

  return { headers, rows } satisfies ParsedCsv
}
